import type { JSX } from 'cdui-js';
import { combineClass, onCleanup } from 'cdui-js';
import './difi-loading.css';

export type DiFiLoadingProps = {
  /** 提示文案，显示在转圈下方；有 children 时仍会显示在自定义内容下方（除非不需要可传空） */
  text?: string;
  /** 尺寸（仅作用于内置转圈） */
  size?: 'sm' | 'md' | 'lg';
  /**
   * - 为 true：整块固定在视口正中央（`position: fixed`，见 `difi-loading--viewport-center`）
   * - 为 false：不占位脱离文档流，随父级普通流排布
   * - `overlay` 为 true 且 `full` 为 true：遮罩层已 `fixed` 铺满，内层用 `difi-loading--full` 在遮罩 flex 中占满可用区域
   */
  full?: boolean;
  /**
   * 全屏遮罩：fixed 铺满视口，用于页面级加载
   */
  overlay?: boolean;
  /**
   * 为 true（默认）且 `overlay` 为 true 时，将遮罩层挂到 `#h5-shell`（无则 `document.body`），
   * 避免业务祖先 `transform`（如 DifiTabs）裁切 fixed，同时让 PC 壳内遮罩对齐壳宽。
   */
  portalToBody?: boolean;
  /**
   * 背景色（任意 CSS 颜色/渐变）
   * - overlay：遮罩层 background
   * - 非 overlay：合并到根节点 style.background（可与 style 并存，显式传入的 background 优先于 style.background）
   */
  background?: string;
  /** 遮罩层 z-index，默认 1000 */
  overlayZIndex?: number;
  /** 追加到遮罩层 class（仅 overlay 时） */
  overlayClass?: string;
  /** 遮罩层行内样式（如 backdropFilter） */
  overlayStyle?: JSX.CSSProperties;
  /**
   * 是否显示内置转圈；设为 false 时可仅用 children / text 做完全自定义
   * @default true
   */
  showSpinner?: boolean;
  /** 自定义中间内容（图标、多行说明等）；顺序为：转圈 → 自定义区 → 文案 */
  children?: JSX.Element;
  /**
   * 与 children 二选一：函数形式自定义内容（便于在父组件内闭包取值）
   */
  contentRender?: () => JSX.Element;
  class?: string;
  style?: JSX.CSSProperties;
};

const sizeClass = {
  sm: 'difi-loading--sm',
  md: 'difi-loading--md',
  lg: 'difi-loading--lg',
} as const;

/** 跟随页面主题 token，暗色下为 #101113，避免首屏/全屏 loading 白底闪烁 */
const DEFAULT_OVERLAY_BG = 'var(--bg-page, #ffffff)';

export const DiFiLoading = (props: DiFiLoadingProps) => {
  const {
    text,
    size = 'md',
    full = false,
    overlay = true,
    portalToBody = true,
    background,
    overlayZIndex = 1000,
    overlayClass = '',
    overlayStyle,
    showSpinner = true,
    children,
    contentRender,
    class: className = '',
    style,
  } = props;

  const custom = contentRender ? contentRender() : children;

  const rootStyle: JSX.CSSProperties = {
    ...style,
    ...(background && !overlay ? { background } : {}),
  };

  const ariaLabel = text ?? '加载中';

  /** overlay 且 portal：挂到壳 / body，避免业务 transform 祖先导致 fixed 失效 */
  let overlayRootEl: HTMLDivElement | null = null;

  const getPortalMount = () =>
    document.getElementById('h5-shell') || document.body;

  const attachOverlayToBody = (el: HTMLDivElement | null) => {
    if (portalToBody && typeof document !== 'undefined' && overlayRootEl && !el) {
      overlayRootEl.remove();
    }
    overlayRootEl = el;
    if (!el || !portalToBody || typeof document === 'undefined') return;
    queueMicrotask(() => {
      const mount = getPortalMount();
      if (el.isConnected && el.parentElement !== mount) {
        mount.appendChild(el);
      }
    });
  };

  onCleanup(() => {
    if (portalToBody && overlayRootEl?.isConnected) {
      overlayRootEl.remove();
    }
    overlayRootEl = null;
  });

  const innerClass = combineClass(
    'difi-loading',
    sizeClass[size],
    overlay ? (full ? 'difi-loading--full' : '') : full ? 'difi-loading--viewport-center' : '',
    className,
  );

  const core = (
    <>
      {showSpinner ? <div class="difi-loading__spinner" aria-hidden="true" /> : null}
      {custom ? <div class="difi-loading__custom">{custom}</div> : null}
      {text ? <div class="difi-loading__text">{text}</div> : null}
    </>
  );

  if (overlay) {
    return (
      <div
        ref={(el) => attachOverlayToBody(el ?? null)}
        class={combineClass('difi-loading__overlay', overlayClass)}
        style={{
          ...overlayStyle,
          'z-index': overlayZIndex,
          background: background ?? DEFAULT_OVERLAY_BG,
        }}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={ariaLabel}
      >
        <div class={innerClass} style={rootStyle}>
          {core}
        </div>
      </div>
    );
  }

  return (
    <div
      class={innerClass}
      style={rootStyle}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      {core}
    </div>
  );
};
